import StyledTableCell from "@components/Table/TableCell";
import StyledTableRow from "@components/Table/TableRow";
import {
  TABLE_BODY_CELL_FONT_SIZE,
  TABLE_HEADER_CELL_FONT_SIZE
} from "@constants/table";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { ClientModelParams } from "@services/api/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface ModelParamsViewerProps {
  data: ClientModelParams;
}

interface TreeNodeProps {
  label: string;
  value: any;
  level?: number;
}

const TreeNode = ({ label, value, level = 0 }: TreeNodeProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(level < 2);
  const isExpandable =
    typeof value === "object" &&
    value !== null &&
    Object.keys(value).length > 0;

  const paddingLeft = level * 24;

  const renderValue = (val: any): React.ReactNode => {
    if (val === null) return <span style={{ color: "#999" }}>null</span>;
    if (val === undefined)
      return <span style={{ color: "#999" }}>undefined</span>;
    if (typeof val === "boolean")
      return <span style={{ color: "#0066cc" }}>{val.toString()}</span>;
    if (typeof val === "number")
      return <span style={{ color: "#098658" }}>{val}</span>;
    if (typeof val === "string")
      return <span style={{ color: "#a31515" }}>"{val}"</span>;
    return val;
  };

  const renderArray = (arr: any[]) => {
    if (arr.length === 0) {
      return (
        <Typography variant="body2" component="span" style={{ color: "#999" }}>
          [ ]
        </Typography>
      );
    }

    // If array contains only primitives, show inline
    if (arr.every((item) => typeof item !== "object")) {
      return (
        <Box component="span" style={{ fontFamily: "monospace" }}>
          {arr.map((item, idx) => (
            <span key={idx} className={idx === 0 ? "ml-32" : "ml-2"}>
              {renderValue(item)}
              {idx < arr.length - 1 && ", "}
            </span>
          ))}
        </Box>
      );
    }

    // For arrays of objects (like summary or errors), show as table
    if (
      arr.length > 0 &&
      typeof arr[0] === "object" &&
      !Array.isArray(arr[0])
    ) {
      const keys = Object.keys(arr[0]);
      return (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            mt: 1,
            mb: 1,
            maxHeight: "calc(100vh - 650px)",
            minHeight: 100
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {keys.map((key) => (
                  <StyledTableCell
                    key={key}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      fontSize: TABLE_HEADER_CELL_FONT_SIZE
                    }}
                  >
                    {key}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {arr.map((item, idx) => (
                <StyledTableRow
                  key={idx}
                  sx={{ "& > *": { borderBottom: "unset" } }}
                >
                  {keys.map((key) => (
                    <StyledTableCell
                      key={key}
                      align="center"
                      sx={{ fontSize: TABLE_BODY_CELL_FONT_SIZE }}
                    >
                      {renderValue(item[key])}
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    // For other arrays, render items as tree nodes
    return (
      <Box>
        {arr.map((item, idx) => (
          <TreeNode
            key={idx}
            label={`[${idx}]`}
            value={item}
            level={level + 1}
          />
        ))}
      </Box>
    );
  };

  const renderObject = (obj: any) => {
    return (
      <Box>
        {Object.entries(obj).map(([key, val]) => (
          <TreeNode key={key} label={key} value={val} level={level + 1} />
        ))}
      </Box>
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          paddingLeft: `${paddingLeft}px`,
          paddingY: 0.5,
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)"
          }
        }}
      >
        {isExpandable ? (
          <IconButton
            size="small"
            onClick={() => setIsOpen(!isOpen)}
            sx={{ marginRight: 1, padding: 0.5 }}
          >
            {isOpen ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 32 }} />
        )}

        <Typography
          variant="body2"
          component="span"
          sx={{ fontWeight: 500, marginRight: 1, fontFamily: "monospace" }}
        >
          {label}:
        </Typography>

        {!isExpandable && (
          <Typography
            variant="body2"
            component="span"
            sx={{ fontFamily: "monospace" }}
          >
            {renderValue(value)}
          </Typography>
        )}

        {isExpandable && !isOpen && (
          <Typography
            variant="body2"
            component="span"
            sx={{ color: "#999", fontStyle: "italic" }}
          >
            {Array.isArray(value)
              ? `${t("modelParamsViewer.array")}[${value.length}]`
              : "{ ... }"}
          </Typography>
        )}
      </Box>

      {isExpandable && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          {Array.isArray(value) ? renderArray(value) : renderObject(value)}
        </Collapse>
      )}
    </Box>
  );
};

const ModelParamsViewer = ({ data }: ModelParamsViewerProps) => {
  return (
    <Box
      className="w-full pt-8  overflow-y-scroll"
      sx={{ maxHeight: "calc(100vh - 450px)", minHeight: 300 }}
    >
      <Paper variant="outlined" sx={{ padding: 2 }}>
        {Object.entries(data).map(([key, value]) => (
          <TreeNode key={key} label={key} value={value} level={0} />
        ))}
      </Paper>
    </Box>
  );
};

export default ModelParamsViewer;
